import general from './general'

function hasAvailableOptions(customization) {
	if (customization?.isAvailable === false) return false
	return (customization?.options || []).some((option) => option?.isAvailable !== false)
}

export function isPlainDish(dish) {
	const selected = dish?.customizations?.selected || []
	const hasSelectedCustomizations = selected.some((item) => {
		if (!item) return false
		if (typeof item === 'object') return item.isAvailable !== false
		return true
	})

	const custom = dish?.customizations?.custom || []
	const hasCustomizations = custom.some(hasAvailableOptions)

	const sideDishes = dish?.sideDishes || []
	const hasSideDishes = sideDishes.some((side) => {
		if (!side) return false
		if (typeof side === 'object') return side.isAvailable !== false
		return true
	})

	return !hasSelectedCustomizations && !hasCustomizations && !hasSideDishes
}

function availableOptions(group) {
	return (group?.options || []).filter((option) => option?.isAvailable !== false)
}

function availableCustomizationGroups(groups) {
	return (groups || []).filter(hasAvailableOptions).map((group) => ({
		...group,
		options: availableOptions(group),
	}))
}

function sideDishGroup(dish) {
	const sides = (dish?.sideDishes || []).filter((side) => {
		if (!side || typeof side !== 'object') return false
		return side.isAvailable !== false
	})
	if (sides.length === 0) return null

	return {
		name: 'Side Dishes',
		isAvailable: true,
		isFixedPrice: true,
		allowsMultiple: false,
		options: sides.map((side) => ({
			name: side.name,
			price: side.price || 0,
			isAvailable: true,
			sideDishId: side._id,
		})),
	}
}

export function getModifierGroups(dish) {
	const selected = availableCustomizationGroups(
		(dish?.customizations?.selected || []).filter((item) => typeof item === 'object'),
	)
	const custom = availableCustomizationGroups(dish?.customizations?.custom || [])
	const groups = [...selected, ...custom]
	const sides = sideDishGroup(dish)
	if (sides) groups.push(sides)
	return groups
}

export function calcOptionPrice(dish, group, option) {
	if (group?.isFixedPrice === false) {
		return general.incrementPriceByPercentage(dish?.price || 0, option?.price || 0)
	}
	return option?.price || 0
}

export function getOrderableVariations(dish) {
	return (dish?.variations || []).filter((v) => v?.isAvailable !== false)
}

export function getSingleOrderableVariation(dish) {
	const orderable = getOrderableVariations(dish)
	return orderable.length === 1 ? orderable[0] : null
}

export function canOneTapAdd(dish) {
	return Boolean(getSingleOrderableVariation(dish)) && isPlainDish(dish)
}

export function isAddDisabledOnOrderRoute(dish) {
	return !(dish?.variations || []).length
}

export function getMenuDisplayPriceLabel(dish) {
	const orderable = getOrderableVariations(dish)
	if (orderable.length > 1) {
		const minPrice = Math.min(...orderable.map((v) => v.price ?? 0))
		return `from ${general.formatPrice(minPrice)}`
	}
	if (orderable.length === 1) {
		const price = orderable[0].price ?? dish?.price
		if (price == null) return null
		return general.formatPrice(price)
	}
	if (dish?.price == null) return null
	return general.formatPrice(dish.price)
}

export function formatDishCardPrice(dish, isOrderRoute) {
	if (isOrderRoute) return getMenuDisplayPriceLabel(dish)
	return dish?.price ? general.formatPrice(dish.price) : null
}

export function buildLineItemId(dishId, variationId, modifiers = []) {
	let base = dishId
	if (variationId) base = `${dishId}-${variationId}`
	if (!modifiers?.length) return base
	const suffix = modifiers
		.map((mod) => mod.externalModifierId || mod.sideDishId || mod.name)
		.join('-')
	return `${base}-${suffix}`
}

export function buildCartLineItem(dish, { variation, modifiers = [] } = {}) {
	const modifierTotal = modifiers.reduce((sum, mod) => sum + (mod.price || 0), 0)
	const catalogVariationId = variation?.externalVariationId ?? null
	const variationName = variation?.name ?? null
	const basePrice = variation != null ? variation.price ?? 0 : dish.price ?? 0
	return {
		lineItemId: buildLineItemId(dish._id, catalogVariationId, modifiers),
		dishId: dish._id,
		name: dish.name,
		variationName,
		basePrice,
		price: basePrice + modifierTotal,
		image: dish.image || dish.restaurant?.logo || null,
		catalogVariationId,
		modifiers,
		quantity: 1,
	}
}

export function buildOrderLineItems(cartItems) {
	return cartItems.map((item) => ({
		name: item.name,
		catalogVariationId: item.catalogVariationId,
		quantity: item.quantity,
		unitPrice: item.basePrice ?? item.price,
		modifiers: (item.modifiers || []).map((mod) => ({
			name: mod.name,
			externalModifierId: mod.externalModifierId,
			unitPrice: mod.price,
		})),
	}))
}
